import {
  ScheduleFrequencies,
  reportScheduleByProjectQuery,
} from '@aims/shared/report-schedules/constants';
import { useQuery } from '@apollo/client';
import { Alert, Card, Empty, Skeleton, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

const { Title, Text } = Typography;

function PreparingStageReportScheduleSection({ projectId }) {
  const { data, loading } = useQuery(reportScheduleByProjectQuery, {
    variables: { holisticProjectId: projectId },
    skip: !projectId,
    fetchPolicy: 'network-only',
  });

  const schedule = useMemo(() => data?.reportScheduleByProject || null, [data]);

  const upcomingEntries = useMemo(() => {
    const now = dayjs();
    const entries = schedule?.entries || [];
    return entries
      .filter((entry) => {
        if ((entry?.status || '').toUpperCase() !== 'UPCOMING') {
          return false;
        }
        if (!entry?.dueDate) {
          return false;
        }
        return dayjs(entry.dueDate).isAfter(now);
      })
      .sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf());
  }, [schedule]);

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          Reporting Schedule
        </Title>
      </div>
      {!schedule ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No reporting schedule has been generated yet."
          />
        </Card>
      ) : (
        <Card>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Read-only schedule preview"
            description="Manage schedule generation and edits from the Reports tab."
          />
          {upcomingEntries.length === 0 ? (
            <Text type="secondary">
              No upcoming report due dates are currently scheduled.
            </Text>
          ) : (
            <Table
              dataSource={upcomingEntries}
              rowKey="entryId"
              pagination={{ pageSize: 8, showSizeChanger: false }}
              size="small"
            >
              <Table.Column title="Label" dataIndex="label" key="label" />
              <Table.Column
                title="Frequency"
                dataIndex="frequency"
                key="frequency"
                width={140}
                render={(frequency) => (
                  <Tag
                    color={ScheduleFrequencies[frequency]?.color || 'default'}
                  >
                    {ScheduleFrequencies[frequency]?.label || frequency}
                  </Tag>
                )}
              />
              <Table.Column
                title="Due Date"
                dataIndex="dueDate"
                key="dueDate"
                width={180}
                render={(dueDate) =>
                  dueDate ? dayjs(dueDate).format('MMM D, YYYY') : '-'
                }
              />
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}

export default PreparingStageReportScheduleSection;
